/**
 * Counting the tills.
 *
 * The float's balance is derived from the ledger like every other balance, and
 * counting it is comparing that to the notes in the drawer. A difference is
 * recorded and posted to a named account — it is never refused for being large
 * and never absorbed for being small.
 *
 * The days-since-counted column is the useful one. A float nobody counts is how
 * a till quietly becomes somebody's income.
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, formatInr } from '../api'
import { useAuth } from '../auth'
import { Card, ErrorNote, Field, Loading, Note } from '../components/ui'

interface Till {
  id: string
  name: string
  location_id: string | null
  per_books_paise: number
  last_counted_on: string | null
  last_difference_paise: number | null
  days_since_counted: number | null
}

interface Count {
  id: string
  bank_account_id: string
  counted_on: string
  counted_paise: number
  per_books_paise: number
  difference_paise: number
  explanation: string | null
  journal_entry_id: string | null
}

const today = () => new Date().toISOString().slice(0, 10)

function toPaise(text: string): number {
  const clean = text.replace(/[^0-9.-]/g, '')
  if (!clean) return 0
  return Math.round(Number(clean) * 100)
}

export default function PettyCash() {
  const { can } = useAuth()
  const queryClient = useQueryClient()
  const [chosen, setChosen] = useState<string | null>(null)
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['tills'] })
    void queryClient.invalidateQueries({ queryKey: ['till-counts'] })
  }

  const tills = useQuery({
    queryKey: ['tills'],
    queryFn: () => api.get<Till[]>('/expenses/tills'),
  })
  const counts = useQuery({
    queryKey: ['till-counts', chosen],
    queryFn: () => api.get<Count[]>(`/expenses/tills/${chosen}/counts`),
    enabled: !!chosen,
  })

  if (tills.isLoading) return <Loading what="Loading tills" />
  const rows = tills.data ?? []
  const current = rows.find((t) => t.id === chosen)

  return (
    <>
      <ErrorNote error={tills.error} />

      <Card title="Cash floats" hint={`${rows.length}`} tight>
        {rows.length === 0 ? (
          <div className="empty">
            No cash floats. A clinic till is a bank account of kind cash — add one under
            bank accounts.
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Till</th>
                <th className="num">Books say</th>
                <th>Last counted</th>
                <th className="num">Last difference</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((till) => (
                <tr key={till.id}>
                  <td>{till.name}</td>
                  <td className="num">{formatInr(till.per_books_paise)}</td>
                  <td className="muted">
                    {till.last_counted_on ? (
                      <>
                        {till.last_counted_on}
                        {till.days_since_counted !== null &&
                          till.days_since_counted > 7 && (
                            <div className="faint">
                              {till.days_since_counted} days ago
                            </div>
                          )}
                      </>
                    ) : (
                      <span className="faint">never</span>
                    )}
                  </td>
                  <td className="num">
                    {till.last_difference_paise ? (
                      formatInr(till.last_difference_paise)
                    ) : (
                      <span className="faint">—</span>
                    )}
                  </td>
                  <td className="num">
                    <button
                      className="small"
                      onClick={() => setChosen(chosen === till.id ? null : till.id)}
                    >
                      Count it
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {current && (
        <>
          {can('exp.petty_cash.reconcile') && (
            <CountForm till={current} onDone={refresh} />
          )}
          <Card
            title={`${current.name} — counted before`}
            hint={`${(counts.data ?? []).length}`}
            tight
            actions={
              <button className="small" onClick={() => setChosen(null)}>
                Close
              </button>
            }
          >
            {(counts.data ?? []).length === 0 ? (
              <div className="empty">This float has never been counted.</div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Counted on</th>
                    <th className="num">In the drawer</th>
                    <th className="num">Books said</th>
                    <th className="num">Difference</th>
                    <th>What happened</th>
                  </tr>
                </thead>
                <tbody>
                  {(counts.data ?? []).map((count) => (
                    <tr key={count.id}>
                      <td className="muted date">{count.counted_on}</td>
                      <td className="num">{formatInr(count.counted_paise)}</td>
                      <td className="num muted">{formatInr(count.per_books_paise)}</td>
                      <td className="num">
                        {count.difference_paise ? (
                          formatInr(count.difference_paise)
                        ) : (
                          <span className="faint">agreed</span>
                        )}
                      </td>
                      <td className="muted">
                        {count.explanation ?? <span className="faint">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </>
      )}
    </>
  )
}

function CountForm({ till, onDone }: { till: Till; onDone: () => void }) {
  const [counted, setCounted] = useState('')
  const [on, setOn] = useState(today())
  const [why, setWhy] = useState('')

  const count = useMutation({
    mutationFn: () =>
      api.post<Count>('/expenses/tills/count', {
        bank_account_id: till.id,
        counted_paise: toPaise(counted),
        counted_on: on,
        explanation: why || null,
      }),
    onSuccess: () => {
      setCounted('')
      setWhy('')
      onDone()
    },
  })

  const difference = counted ? toPaise(counted) - till.per_books_paise : 0

  return (
    <Card title={`Count ${till.name}`} hint="what is actually in the drawer">
      <ErrorNote error={count.error} />
      <form
        onSubmit={(e) => {
          e.preventDefault()
          count.mutate()
        }}
      >
        <div className="row">
          <Field label="Counted on">
            <input type="date" value={on} onChange={(e) => setOn(e.target.value)} required />
          </Field>
          <Field label="In the drawer (₹)" caption={`the books say ${formatInr(till.per_books_paise)}`}>
            <input
              className="num"
              value={counted}
              onChange={(e) => setCounted(e.target.value)}
              placeholder="0.00"
              required
            />
          </Field>
          <Field label="What do you think happened" caption="required if they disagree">
            <input value={why} onChange={(e) => setWhy(e.target.value)} />
          </Field>
          <div className="shrink">
            <button className="primary" type="submit" disabled={count.isPending}>
              Record the count
            </button>
          </div>
        </div>

        {counted !== '' && difference !== 0 && (
          <Note kind="warn">
            The drawer is {formatInr(Math.abs(difference))}{' '}
            {difference < 0 ? 'short' : 'over'}. That is a real gain or loss and will be
            posted to the cash short-or-over account, so say what you think happened.
          </Note>
        )}
        {counted !== '' && difference === 0 && (
          <Note kind="ok">
            The drawer agrees with the books. Nothing will be posted, and the count is
            still worth recording.
          </Note>
        )}
      </form>
    </Card>
  )
}
